import React, { useEffect, useRef, useState } from "react";
import { Modal, Form, Input, Button, message, InputNumber, DatePicker, Spin, Empty, Card, Tooltip, Tabs, Segmented } from "antd";
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import dayjs from "dayjs";
import { MakeApiCall } from "../../../../apis";
import { Wrapper } from "../style";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Format date and time
const formatDateTime = (ts) =>
  new Date(ts * 1000).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

// Format just date for x-axis
const formatDateForXAxis = (ts) =>
  new Date(ts * 1000).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short'
  });

// Format with full date and time for tooltip
const formatTooltipDateTime = (ts) =>
  new Date(ts * 1000).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

const AddCogs = ({ visible, onClose, onSave, getList, product, userData, isUpdate, sku, marketplaceId }) => {

  const [form] = Form.useForm();
  const [cogsHistory, setCogsHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeRightTab, setActiveRightTab] = useState("chart");
  const chartRef = useRef(null);

  useEffect(() => {
    if (visible && product) {
      form.setFieldsValue({
        item_cogs: product?.item_cogs || "",
        item_cogs_batch_start_date: product?.item_cogs_batch_start_date
          ? dayjs(product.item_cogs_batch_start_date)
          : null,
      });
      
      // Fetch COGS history when modal opens
      if (sku || product?.sku) {
        fetchCogsHistory();
      }
    } else {
      form.resetFields();
      setCogsHistory([]);
      setError(null);
      setActiveRightTab("chart");
    }
  }, [visible, product]);

  const fetchCogsHistory = async () => {
    setLoadingHistory(true);
    setError(null);
    try {
      const productSku = sku || product?.sku;
      if (!productSku) return;

      const response = await MakeApiCall(
        'products/cogs-history',
        'post',
        { sku: productSku },
        true,
        null,
        userData?.token
      );

      if (response?.status && response?.data?.records) {
        // Format data for chart - sort by created_at (oldest to newest)
        const sortedRecords = [...response.data.records].sort((a, b) => a.created_at - b.created_at);
        
        const formattedData = sortedRecords.map(record => ({
          id: record.id,
          timestamp: record.created_at,
          dateTime: formatDateTime(record.created_at),
          dateForXAxis: formatDateForXAxis(record.created_at),
          cogs: parseFloat(record.item_cogs),
          batchStartDate: record.item_cogs_batch_start_date,
          currency: record.currency || 'INR',
          marketplace_id: record.marketplace_id
        }));
        
        setCogsHistory(formattedData);
      } else {
        setCogsHistory([]);
      }
    } catch (error) {
      console.error("Failed to fetch COGS history:", error);
      setError("Failed to load COGS history");
      setCogsHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        setSubmitting(true);
        const formattedData = {
          item_cogs: values.item_cogs,
          item_cogs_batch_start_date: values.item_cogs_batch_start_date
            ? dayjs(values.item_cogs_batch_start_date).format('YYYY-MM-DD')
            : null,
        };

        try {
          let response;
          if (sku) {
            // Dashboard / SKU-based mode
            const payload = {
              sku,
              item_cogs: formattedData.item_cogs,
              item_cogs_batch_start_date: formattedData.item_cogs_batch_start_date,
              ...(marketplaceId ? { marketplace_id: marketplaceId } : {}),
            };
            response = await MakeApiCall('products/update-sku-cogs', 'post', payload, true, null, userData?.token);
          } else {
            // Products-page mode
            response = await MakeApiCall(`update-product/${product?.id}`, 'post', formattedData, true, null, userData?.token);
          }

          if (response?.status) {
            message.success(response?.message);
            // Refresh form with new values
            form.setFieldsValue({
              item_cogs: values.item_cogs,
              item_cogs_batch_start_date: values.item_cogs_batch_start_date
                ? dayjs(values.item_cogs_batch_start_date)
                : null,
            });
            // Update product object with new values
            if (product) {
              product.item_cogs = values.item_cogs;
              product.item_cogs_batch_start_date = formattedData.item_cogs_batch_start_date;
            }
            getList && getList();
            // Refresh history after update
            await fetchCogsHistory();
            message.success("COGS updated successfully and history refreshed");
          } else {
            message.error(response?.message);
          }
        } catch (error) {
          message.error("Something went wrong. Please try again.");
          console.error("Request Failed:", error);
        } finally {
          setSubmitting(false);
        }
      })
      .catch((errorInfo) => {
        message.error("Validation Failed");
        console.log("Validation failed:", errorInfo);
      });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose(false);
  };

  // Prepare chart data
  const hasData = cogsHistory.length > 0;
  const cogsColor = '#314F71';

  const chartData = {
    labels: hasData ? cogsHistory.map(record => record.dateForXAxis) : [],
    datasets: [
      {
        label: 'COGS (Cost per unit)',
        data: hasData ? cogsHistory.map(record => record.cogs) : [],
        borderColor: cogsColor,
        // backgroundColor: 'rgba(49, 79, 113, 0.1)',
        borderWidth: 2.5,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: cogsColor,
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        fill: false,
      }
    ]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            if (cogsHistory[index]) {
              return formatTooltipDateTime(cogsHistory[index].timestamp);
            }
            return tooltipItems[0].label;
          },
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            const value = context.parsed.y;
            if (value === null || value === undefined) {
              label += '—';
            } else {
              label += `₹${value.toFixed(2)}`;
            }
            return label;
          },
          afterBody: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            if (cogsHistory[index] && cogsHistory[index].batchStartDate) {
              return [`Batch Start: ${cogsHistory[index].batchStartDate}`];
            }
            return [];
          }
        },
        backgroundColor: 'white',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: '#ddd',
        borderWidth: 1,
        padding: 10,
        displayColors: true,
      },
      legend: {
        position: 'top',
        align: 'center',
        labels: {
          font: {
            size: 11,
          },
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          padding: 10,
        }
      },
    },
    scales: {
      x: {
        // title: {
        //   display: true,
        //   text: 'Date',
        //   color: '#666',
        //   font: {
        //     size: 11,
        //   }
        // },
        ticks: {
          font: {
            size: 10,
          },
          color: '#888',
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: 8,
        },
        grid: {
          display: false,
        },
      },
      y: {
        position: 'left',
        // title: {
        //   display: true,
        //   text: 'COGS (₹)',
        //   color: cogsColor,
        //   font: {
        //     size: 11,
        //     weight: '500',
        //   }
        // },
        ticks: {
          callback: (value) => {
            if (value === null || value === undefined) return '—';
            return `₹${value.toFixed(2)}`;
          },
          font: {
            size: 10,
          },
          color: cogsColor,
        },
        grid: {
          color: '#f0f0f0',
          drawBorder: false,
          lineWidth: 1,
        },
      }
    },
    elements: {
      line: {
        borderJoin: 'round',
        borderCap: 'round',
      },
      point: {
        hoverRadius: 6,
        radius: 3,
      }
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 10,
        right: 10
      }
    }
  };

  // Calculate statistics
  const latestCogs = hasData ? cogsHistory[cogsHistory.length - 1].cogs : null;
  const highestCogs = hasData ? Math.max(...cogsHistory.map(r => r.cogs)) : null;
  const lowestCogs = hasData ? Math.min(...cogsHistory.map(r => r.cogs)) : null;
  const averageCogs = hasData ? (cogsHistory.reduce((sum, r) => sum + r.cogs, 0) / cogsHistory.length).toFixed(2) : null;

  // Right side tabs items
  const rightTabItems = [
    {
      key: 'chart',
      label: '📈 Chart View',
      children: (
        <div style={{ minHeight: 350 }}>
          {loadingHistory && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 350 }}>
              <Spin size="large" tip="Loading COGS history..." />
            </div>
          )}

          {!loadingHistory && error && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 350 }}>
              <Empty description={error} />
            </div>
          )}

          {!loadingHistory && !error && !hasData && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 350 }}>
              <Empty description="No COGS history available for this product" />
            </div>
          )}

          {!loadingHistory && !error && hasData && (
            <>
              {/* Stats Cards */}
              <div className="d-flex gap-2 mb-3 flex-wrap d-none">
                <Card
                  size="small"
                  style={{
                    flex: 1, minWidth: 120,
                    borderRadius: 8, background: '#EEF3FA',
                    borderLeft: `3px solid ${cogsColor}`,
                  }}
                  bodyStyle={{ padding: '6px 10px' }}
                >
                  <div className="fs-7 text-muted mb-1">Latest</div>
                  <div className="fw-bold fs-4" style={{ color: cogsColor, lineHeight: 1.2 }}>
                    ₹{latestCogs?.toFixed(2)}
                  </div>
                </Card>

                <Card
                  size="small"
                  style={{
                    flex: 1, minWidth: 120,
                    borderRadius: 8, background: '#FEF0EB',
                    borderLeft: '3px solid #f45b68',
                  }}
                  bodyStyle={{ padding: '6px 10px' }}
                >
                  <div className="fs-7 text-muted mb-1">Highest</div>
                  <div className="fw-bold fs-4" style={{ color: '#f45b68', lineHeight: 1.2 }}>
                    ₹{highestCogs?.toFixed(2)}
                  </div>
                </Card>

                <Card
                  size="small"
                  style={{
                    flex: 1, minWidth: 120,
                    borderRadius: 8, background: '#E8F5E9',
                    borderLeft: '3px solid #4caf50',
                  }}
                  bodyStyle={{ padding: '6px 10px' }}
                >
                  <div className="fs-7 text-muted mb-1">Lowest</div>
                  <div className="fw-bold fs-4" style={{ color: '#4caf50', lineHeight: 1.2 }}>
                    ₹{lowestCogs?.toFixed(2)}
                  </div>
                </Card>

                <Card
                  size="small"
                  style={{
                    flex: 1, minWidth: 120,
                    borderRadius: 8, background: '#F4F6F9',
                    borderLeft: '3px solid #adb5bd',
                  }}
                  bodyStyle={{ padding: '6px 10px' }}
                >
                  <div className="fs-7 text-muted mb-1">Average</div>
                  <div className="fw-bold fs-4" style={{ color: '#6c757d', lineHeight: 1.2 }}>
                    ₹{averageCogs}
                  </div>
                </Card>
              </div>

              {/* Chart */}
              <Line
                ref={chartRef}
                data={chartData}
                options={chartOptions}
                height={150}
              />
            </>
          )}
        </div>
      )
    },
    {
      key: 'table',
      label: '📋 Table View',
      children: (
        <div>
          {loadingHistory && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <Spin size="large" tip="Loading COGS history..." />
            </div>
          )}

          {!loadingHistory && error && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <Empty description={error} />
            </div>
          )}

          {!loadingHistory && !error && !hasData && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <Empty description="No COGS history available for this product" />
            </div>
          )}

          {!loadingHistory && !error && hasData && (
            <>
              {/* Stats Cards */}
              <div className="d-flex gap-2 mb-3 flex-wrap d-none">
                <Card
                  size="small"
                  style={{
                    flex: 1, minWidth: 120,
                    borderRadius: 8, background: '#EEF3FA',
                    borderLeft: `3px solid ${cogsColor}`,
                  }}
                  bodyStyle={{ padding: '6px 10px' }}
                >
                  <div className="fs-7 text-muted mb-1">Total Records</div>
                  <div className="fw-bold fs-4" style={{ color: cogsColor, lineHeight: 1.2 }}>
                    {cogsHistory.length}
                  </div>
                </Card>

                <Card
                  size="small"
                  style={{
                    flex: 1, minWidth: 120,
                    borderRadius: 8, background: '#FEF0EB',
                    borderLeft: '3px solid #f45b68',
                  }}
                  bodyStyle={{ padding: '6px 10px' }}
                >
                  <div className="fs-7 text-muted mb-1">Current COGS</div>
                  <div className="fw-bold fs-4" style={{ color: '#f45b68', lineHeight: 1.2 }}>
                    ₹{latestCogs?.toFixed(2)}
                  </div>
                </Card>

                <Card
                  size="small"
                  style={{
                    flex: 1, minWidth: 120,
                    borderRadius: 8, background: '#F4F6F9',
                    borderLeft: '3px solid #adb5bd',
                  }}
                  bodyStyle={{ padding: '6px 10px' }}
                >
                  <div className="fs-7 text-muted mb-1">Date Range</div>
                  <div className="fw-bold fs-6" style={{ color: '#6c757d', lineHeight: 1.2 }}>
                    {hasData && (
                      <>
                        {cogsHistory[0]?.dateTime.split(',')[0]} <br />
                        to {cogsHistory[cogsHistory.length - 1]?.dateTime.split(',')[0]}
                      </>
                    )}
                  </div>
                </Card>
              </div>

              {/* Data Table */}
              <div style={{
                maxHeight: '350px',
                overflow: 'auto',
                border: '1px solid #f0f0f0',
                borderRadius: '6px'
              }}>
                <table className="table table-sm table-hover" style={{ fontSize: '12px', marginBottom: 0 }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#fafafa', zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: '10px', width: '50px' }}>#</th>
                      <th style={{ padding: '10px' }}>Date & Time</th>
                      <th style={{ padding: '10px' }}>COGS (₹)</th>
                      <th style={{ padding: '10px' }}>Batch Start Date</th>
                      <th style={{ padding: '10px' }}>Currency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cogsHistory.slice().reverse().map((record, idx) => (
                      <tr key={record.id}>
                        <td style={{ padding: '8px' }}>{idx + 1}</td>
                        <td style={{ padding: '8px' }}>{record.dateTime}</td>
                        <td style={{ padding: '8px' }}>
                          <span className="fw-semibold" style={{ color: cogsColor }}>
                            ₹{record.cogs.toFixed(2)}
                          </span>
                        </td>
                        <td style={{ padding: '8px' }}>{record.batchStartDate || '-'}</td>
                        <td style={{ padding: '8px' }}>{record.currency}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <Modal
      title={
        <div className="d-flex flex-column gap-1">
          <span className="fw-bold fs-4 mb-1 text-gray-900">Update COGS</span>
          <div className="fs-7 d-flex flex-wrap gap-2">
            <span><b>Product:</b> {product?.item_name || product?.product_title || product?.name}</span>
            <span className="ms-2"><b>SKU:</b> {sku || product?.sku}</span>
            {marketplaceId && <span className="ms-2"><b>Marketplace:</b> {marketplaceId}</span>}
          </div>
        </div>
      }
      open={visible}
      width={1200}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      centered
      bodyStyle={{ padding: '20px' }}
    >
      <Wrapper>
        <div className="row">
          {/* Left Side - Update COGS Section */}
          <div className="col-md-5" style={{ borderRight: '1px solid #e8e8e8', paddingRight: '24px' }}>
            <div className="d-flex justify-content-between align-items-center mb-4 min-h-40px">
              <h5 style={{ color: '#314F71', fontWeight: 600, margin: 0 }}>Update COGS</h5>
              {/* <Tooltip title="Refresh history">
                <button 
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => fetchCogsHistory()}
                  style={{ borderRadius: '6px' }}
                >
                  <i className="ki-outline ki-arrows-circle"></i>
                </button>
              </Tooltip> */}
            </div>

            <Form form={form} onFinish={handleFormSubmit} layout="vertical">
              <div className="mb-4">
                <label className="fs-6 fw-bold mb-2">
                  Cost per unit <span style={{ color: '#ff4d4f' }}>*</span>
                </label>
                <Form.Item
                  name="item_cogs"
                  className='mb-0'
                  rules={[{ required: true, message: 'Please enter Cost' }]}
                >
                  <InputNumber
                    className="w-100"
                    variant="filled"
                    size='large'
                    min={0}
                    precision={2}
                    placeholder="Enter cost per unit"
                    formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    onKeyDown={(e) => {
                      const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter', '.'];
                      if (allowed.includes(e.key)) return;
                      if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase())) return;
                      if (e.key < '0' || e.key > '9') { e.preventDefault(); }
                    }}
                  />
                </Form.Item>
              </div>
              
              <div className="mb-4">
                <label className="fs-6 fw-bold mb-2">Start Date (Optional)</label>
                <Form.Item
                  name="item_cogs_batch_start_date"
                  className='mb-0'
                >
                  <DatePicker
                    className="w-100"
                    variant="filled"
                    size='large'
                    format="YYYY-MM-DD"
                    placeholder="Select Start Date"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <small className="text-muted">Date from which this COGS becomes effective</small>
              </div>

              {/* Current COGS Info */}
              {product?.item_cogs && (
                <div className="mt-4 p-3" style={{ background: '#f8f9fa', borderRadius: '8px' }}>
                  <div className="text-muted mb-1">Current COGS</div>
                  <div className="fw-bold fs-4" style={{ color: '#314F71' }}>
                    ₹{parseFloat(product.item_cogs).toFixed(2)}
                  </div>
                  {product?.item_cogs_batch_start_date && (
                    <div className="text-muted small mt-1">
                      Effective from: {product.item_cogs_batch_start_date}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-4 d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={handleCancel}
                  style={{ padding: '8px 20px', borderRadius: '6px' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => form.submit()}
                  disabled={submitting}
                  style={{ padding: '8px 20px', borderRadius: '6px' }}
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </Form>
          </div>

          {/* Right Side - History Section with Tabs */}
          <div className="col-md-7" style={{ paddingLeft: '24px' }}>
            <div className="d-flex justify-content-between align-items-center mb-3 ">
              <h5 style={{ color: '#314F71', fontWeight: 600, margin: 0 }}>
                COGS History
              </h5>
              <Segmented
                size="middle"
                options={[
                  {
                    label: (
                      <span>
                         Chart View
                      </span>
                    ),
                    value: 'chart'
                  },
                  {
                    label: (
                      <span>
                        Table View
                      </span>
                    ),
                    value: 'table'
                  }
                ]}
           
                value={activeRightTab}
                onChange={setActiveRightTab}
                style={{ marginBottom: 0 }}
              />
            </div>
            
            <div style={{ marginTop: '8px' }}>
              
              {rightTabItems.find(tab => tab.key === activeRightTab)?.children}
            </div>
      
          </div>
        </div>
      </Wrapper>
    </Modal>
  );
};

export default AddCogs;